import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPublicClient, http } from "viem";
import { useAllSeasons } from "@/hooks/useAllSeasons";
import { useSeasonWinnerSummaries } from "@/hooks/useSeasonWinnerSummaries";
import { useAccount, useChains } from "wagmi";
import { useLoginModal } from "@/hooks/useLoginModal";
import { SOFBondingCurveAbi } from "@/utils/abis";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { usePlatform } from "@/hooks/usePlatform";
import MobileRafflesList from "@/components/mobile/MobileRafflesList";
import SeasonCardSkeleton from "@/components/common/skeletons/SeasonCardSkeleton";
import { useState, useCallback, useMemo } from "react";
import BuySellSheet from "@/components/mobile/BuySellSheet";
import { useSeasonGating, GateType } from "@/hooks/useSeasonGating";
import PasswordGateModal from "@/components/gating/PasswordGateModal";
import SignatureGateModal from "@/components/gating/SignatureGateModal";
import { useProfileData } from "@/hooks/useProfileData";
import { SeasonCard } from "@/components/raffles/SeasonCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFirstViewGateBatch } from "@/hooks/useFirstViewGate";

/**
 * Map an on-chain SeasonStatus enum value to its tab group name.
 * Spec: docs/superpowers/specs/2026-05-10-raffle-list-design-upgrade-design.md
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getSeasonGroup(statusNum) {
  const n = Number(statusNum);
  if (n === 0) return "upcoming";
  if (n === 1) return "active";
  if (n === 2 || n === 3 || n === 4) return "settling";
  if (n === 5 || n === 6) return "complete";
  return "active";
}

const VALID_TABS = ["upcoming", "active", "settling", "complete"];

const RaffleList = () => {
  const { t } = useTranslation(["raffle", "navigation"]);
  const { isMobile } = usePlatform();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { address, isConnected, chainId } = useAccount();
  const chains = useChains();
  const { openLoginModal } = useLoginModal();
  const allSeasonsQuery = useAllSeasons();
  // Track which tab is active so winner-summary reads only fire when the
  // user is actually looking at the Complete tab. Completed seasons hold
  // immutable data, but the two multicalls (winners + payouts) still cost
  // an RPC round-trip on every cold visit if we pre-fetch them.
  // activeTab is URL-backed (?tab=<group>) so it persists across reloads,
  // share-links, and stays consistent between desktop and mobile views.
  // Invalid or missing values fall back to "active". The default is omitted
  // from the URL to keep links clean.
  const urlTab = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(urlTab) ? urlTab : "active";
  const setActiveTab = useCallback(
    (next) => {
      setSearchParams((prev) => {
        if (next === "active") {
          prev.delete("tab");
        } else {
          prev.set("tab", next);
        }
        return prev;
      });
    },
    [setSearchParams],
  );
  const winnerSummariesQuery = useSeasonWinnerSummaries(
    allSeasonsQuery.data,
    { enabled: activeTab === "complete" },
  );

  // "My Raffles" filter — initialized from ?filter=mine URL param
  const [showMineOnly, setShowMineOnly] = useState(
    searchParams.get("filter") === "mine",
  );
  const { seasonBalancesQuery } = useProfileData(address);
  const ownedSeasonIds = useMemo(() => {
    const ids = new Set();
    if (seasonBalancesQuery.data) {
      for (const s of seasonBalancesQuery.data) {
        ids.add(Number(s.seasonId));
      }
    }
    return ids;
  }, [seasonBalancesQuery.data]);

  const handleToggleMine = useCallback(
    (checked) => {
      setShowMineOnly(checked);
      setSearchParams((prev) => {
        if (checked) {
          prev.set("filter", "mine");
        } else {
          prev.delete("filter");
        }
        return prev;
      });
    },
    [setSearchParams],
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState("buy");
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [localPosition, setLocalPosition] = useState({
    tickets: 0n,
    probBps: 0,
    total: 0n,
  });
  const [activeSeason, setActiveSeason] = useState(null);
  const [gateModalOpen, setGateModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "buy" | "sell" | null

  // Gating hook — tracks the carousel-visible season
  const isActiveGated = Boolean(activeSeason?.config?.gated);
  const { isVerified, verifyPassword, verifySignature, gates, refetch: refetchGating } = useSeasonGating(
    activeSeason?.id, { isGated: isActiveGated }
  );

  const pendingGateType = useMemo(() => {
    if (!gates || gates.length === 0) return null;
    return Number(gates[0].gateType);
  }, [gates]);

  const handleActiveSeasonChange = useCallback((season) => {
    setActiveSeason(season);
  }, []);

  const handleConnect = useCallback(() => {
    openLoginModal();
  }, [openLoginModal]);

  const renderBadge = (st) => {
    const statusNum = Number(st);
    const { label, variant } = (() => {
      switch (statusNum) {
        case 0: return { label: t("notStarted"), variant: "statusUpcoming" };
        case 1: return { label: t("active"), variant: "statusActive" };
        case 2: return { label: t("settlingAwaitingEnd"), variant: "statusSettling" };
        case 3: return { label: t("settlingDrawingWinner"), variant: "statusSettling" };
        case 4: return { label: t("settlingDistributing"), variant: "statusSettling" };
        case 5: return { label: t("completed"), variant: "statusCompleted" };
        case 6: return { label: t("cancelled"), variant: "statusDanger" };
        default: return { label: t("unknownStatus"), variant: "statusCompleted" };
      }
    })();
    return <Badge variant={variant}>{label}</Badge>;
  };

  const handleBuy = (seasonId) => {
    const season = allSeasonsQuery.data?.find((s) => s.id === seasonId);
    setSelectedSeason(season);

    // Gating check: if gated and not verified, show password modal
    if (season?.config?.gated && isVerified !== true) {
      setPendingAction("buy");
      setGateModalOpen(true);
      return;
    }

    setSheetMode("buy");
    setSheetOpen(true);
  };

  const handleSell = async (seasonId) => {
    const season = allSeasonsQuery.data?.find((s) => s.id === seasonId);
    setSelectedSeason(season);

    // Gating check: if gated and not verified, show password modal
    if (season?.config?.gated && isVerified !== true) {
      setPendingAction("sell");
      setGateModalOpen(true);
      return;
    }

    await openSellSheet(season);
  };

  // Called from SeasonCard's Verify button (no pending buy/sell action)
  const handleVerify = useCallback((seasonId) => {
    const season = allSeasonsQuery.data?.find((s) => s.id === seasonId);
    setSelectedSeason(season);
    setPendingAction(null);
    setGateModalOpen(true);
  }, [allSeasonsQuery.data]);

  // MobileRafflesList onVerify — fires Verify on the currently-active season.
  const handleVerifyActive = useCallback(() => {
    if (activeSeason) handleVerify(activeSeason.id);
  }, [activeSeason, handleVerify]);

  // Fetch position and open sell sheet (bypasses gating check)
  const openSellSheet = useCallback(async (season) => {
    const bondingCurveAddress =
      season?.config?.bondingCurve || season?.bondingCurveAddress;

    if (bondingCurveAddress && chainId) {
      const currentChain = chains.find((chain) => chain.id === chainId);
      if (currentChain?.rpcUrls?.default) {
        const rpcUrl = currentChain.rpcUrls.default.http?.[0];
        if (rpcUrl) {
          const positionClient = createPublicClient({
            chain: currentChain,
            transport: http(rpcUrl),
            blockTag: "latest",
          });
          try {
            const [pt, cfg] = await Promise.all([
              positionClient.readContract({
                address: bondingCurveAddress,
                abi: SOFBondingCurveAbi,
                functionName: "playerTickets",
                args: [address],
              }),
              positionClient.readContract({
                address: bondingCurveAddress,
                abi: SOFBondingCurveAbi,
                functionName: "curveConfig",
                args: [],
              }),
            ]);
            const tickets = BigInt(pt ?? 0n);
            const total = BigInt(cfg?.[0] ?? cfg?.totalSupply ?? 0n);
            const probBps = total > 0n ? Number((tickets * 10000n) / total) : 0;
            setLocalPosition({ tickets, probBps, total });
          } catch {
            // ignore
          }
        }
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 200));
    setSheetMode("sell");
    setSheetOpen(true);
  }, [address, chainId, chains]);

  // Called after successful password verification
  const handleGateVerified = useCallback(async () => {
    await refetchGating();
    if (pendingAction === "buy" && selectedSeason) {
      setSheetMode("buy");
      setSheetOpen(true);
    } else if (pendingAction === "sell" && selectedSeason) {
      await openSellSheet(selectedSeason);
    }
    // If pendingAction is null (standalone verify), just close — badge updates reactively
    setPendingAction(null);
  }, [refetchGating, pendingAction, selectedSeason, openSellSheet]);

  // BuySellSheet onSuccess — close and navigate to the season's detail page.
  const handleSheetSuccess = useCallback(async () => {
    setSheetOpen(false);
    if (selectedSeason) {
      navigate(`/raffles/${selectedSeason.id}`);
    }
  }, [navigate, selectedSeason]);

  // Mobile view (phones, touch tablets)
  const displayedSeasons = useMemo(() => {
    const sorted = [...(allSeasonsQuery.data || [])].sort(
      (a, b) => Number(b.id) - Number(a.id),
    );
    return showMineOnly && isConnected
      ? sorted.filter((s) => ownedSeasonIds.has(Number(s.id)))
      : sorted;
  }, [allSeasonsQuery.data, showMineOnly, isConnected, ownedSeasonIds]);

  // Bucket the (already filtered) displayed seasons into the four tab groups.
  // Completed/cancelled seasons the viewer hasn't seen yet in the celebration
  // modal are demoted to 'settling' so they surface before moving to 'complete'.
  const seasonIds = useMemo(
    () => displayedSeasons.map((s) => s.id),
    [displayedSeasons],
  );
  const seenSet = useFirstViewGateBatch('celebrated', seasonIds);

  const grouped = useMemo(() => {
    const buckets = { upcoming: [], active: [], settling: [], complete: [] };
    for (const s of displayedSeasons) {
      let g = getSeasonGroup(s.status);
      const demotedToSettling =
        g === 'complete' && !seenSet.has(String(s.id));
      if (demotedToSettling) g = 'settling';
      // Only status 5 (Completed with winner) carries a spoiler. Cancelled
      // (status 6) already says "no payout" which matches the celebration's
      // cancelled variant — no suppression needed.
      const suppressWinner = demotedToSettling && Number(s.status) === 5;
      if (buckets[g]) buckets[g].push({ season: s, suppressWinner });
    }
    return buckets;
  }, [displayedSeasons, seenSet]);

  if (isMobile) {
    // Note: We pass raw season data and let MobileRafflesList handle curve state
    // This avoids calling hooks inside map/filter which violates Rules of Hooks
    return (
      <>
        <MobileRafflesList
          grouped={grouped}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isLoading={allSeasonsQuery.isLoading}
          onBuy={handleBuy}
          onSell={handleSell}
          onActiveSeasonChange={handleActiveSeasonChange}
          isVerified={isVerified}
          isGated={isActiveGated}
          onVerify={handleVerifyActive}
          isConnected={isConnected}
          onConnect={handleConnect}
          showMineOnly={showMineOnly}
          onToggleMine={handleToggleMine}
        />
        {selectedSeason && (
          <BuySellSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            mode={sheetMode}
            seasonId={selectedSeason.id}
            seasonStatus={selectedSeason.status}
            seasonEndTime={selectedSeason.config?.endTime}
            bondingCurveAddress={selectedSeason.config?.bondingCurve}
            maxSellable={localPosition?.tickets || 0n}
            onSuccess={handleSheetSuccess}
          />
        )}
        {pendingGateType === GateType.SIGNATURE ? (
          <SignatureGateModal
            open={gateModalOpen}
            onOpenChange={setGateModalOpen}
            seasonId={selectedSeason?.id || activeSeason?.id}
            seasonName={selectedSeason?.config?.name || activeSeason?.config?.name}
            userAddress={address}
            verifySignature={verifySignature}
            onVerified={handleGateVerified}
          />
        ) : (
          <PasswordGateModal
            open={gateModalOpen}
            onOpenChange={setGateModalOpen}
            seasonName={selectedSeason?.config?.name || activeSeason?.config?.name}
            onVerify={verifyPassword}
            onVerified={handleGateVerified}
          />
        )}
      </>
    );
  }

  // Desktop view
  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-4">{t("title")}</h1>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{t("allSeasons")}</h2>
            <p className="text-sm text-muted-foreground">{t("allSeasonsDescription")}</p>
          </div>
          {isConnected && (
            <div className="flex items-center gap-2">
              <Switch
                checked={showMineOnly}
                onCheckedChange={handleToggleMine}
                id="desktop-mine-toggle"
              />
              <label
                htmlFor="desktop-mine-toggle"
                className="text-sm text-muted-foreground cursor-pointer select-none"
              >
                {t("navigation:myRaffles")}
              </label>
            </div>
          )}
        </div>
        {allSeasonsQuery.isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SeasonCardSkeleton key={i} />
            ))}
          </div>
        )}
        {allSeasonsQuery.error && <p>Error loading seasons.</p>}
        {displayedSeasons.length === 0 && !allSeasonsQuery.isLoading && (
          <p>{t("noActiveSeasons")}</p>
        )}
        {!allSeasonsQuery.isLoading && displayedSeasons.length > 0 && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              {["upcoming", "active", "settling", "complete"].map((g) => {
                const count = grouped[g].length;
                return (
                  <TabsTrigger
                    key={g}
                    value={g}
                    className="flex items-center gap-2"
                  >
                    <span>{t(`tabs.${g}`)}</span>
                    <span
                      className="inline-flex items-center justify-center min-w-[1.5rem] rounded-full border border-border px-2 text-xs font-semibold leading-5
                                 bg-secondary text-secondary-foreground
                                 [[data-state=active]_&]:bg-background
                                 [[data-state=active]_&]:text-primary"
                    >
                      {count}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {["upcoming", "active", "settling", "complete"].map((g) => (
              <TabsContent key={g} value={g}>
                {grouped[g].length === 0 ? (
                  <p className="text-muted-foreground py-8 text-center">
                    {t(`emptyTab.${g}`)}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {grouped[g].map(({ season, suppressWinner }) => (
                      <SeasonCard
                        key={season.id}
                        season={season}
                        renderBadge={renderBadge}
                        winnerSummary={winnerSummariesQuery.data?.[season.id]}
                        suppressWinner={suppressWinner}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default RaffleList;
